import { Flex, Text, Loader, Badge } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import colors from '../../utils/colors';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInFromLeft, fadeInFromTop } from '../../utils/animations';
import { useState, useEffect } from 'react';
import { useGroupClasses, GroupClassEntry } from '../../hooks/useGroupClasses';
import { IconCalendarEvent, IconClock, IconUsers, IconCheck, IconUser, IconChevronRight } from '@tabler/icons-react';

const ClassCard: React.FC<{ entry: GroupClassEntry; onClick: () => void }> = ({ entry, onClick }) => {
  const { translate } = useLanguage();
  const date = new Date(entry.date);
  const spotsLeft = entry.maxMembers - entry.signupCount;

  return (
    <motion.div
      variants={fadeInFromLeft}
      className="overflow-hidden"
      style={{ width: '100%' }}
    >
      <div
        onClick={onClick}
        className="relative rounded-xl p-4 mb-2 cursor-pointer active:scale-[0.98] transition-transform"
        style={{
          background: 'white',
          borderLeft: `5px solid ${entry.color}`,
          borderRight: entry.isSignedUp ? `4px solid ${colors.green}` : undefined,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1 }}>
          {entry.isSignedUp && (
            <div
              className="absolute top-2 right-8 flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold"
              style={{
                backgroundColor: `${colors.green}20`,
                color: colors.green,
                fontSize: 10,
              }}
            >
              <IconCheck size={10} />
              {translate('signedUp')}
            </div>
          )}

          <span className="font-semibold text-custom36p text-lg">
            {entry.title}
          </span>

          {entry.host && (
            <Flex align="center" gap={4} mt={4}>
              <IconUser size={14} color={colors.primary} />
              <Text size="xs" fw={500} c={colors.custom36s}>
                {entry.host}
              </Text>
            </Flex>
          )}

          <Flex gap={16} align="center" mt={entry.host ? 4 : 6}>
            <Flex align="center" gap={4}>
              <IconCalendarEvent size={15} color={colors.primary} />
              <Text size="xs" fw={600} c={colors.custom36s}>
                {date.getDate()} {date.toLocaleDateString('pl-PL', { month: 'short' })}
              </Text>
              <Text size="xs" fw={500} c={colors.custom36s}>
                ({date.toLocaleDateString('pl-PL', { weekday: 'short' })})
              </Text>
              <Text size="xs" fw={600} c={colors.custom36s}>
                {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </Flex>

            <Flex align="center" gap={4}>
              <IconClock size={15} color={colors.primary} />
              <Text size="xs" fw={500} c={colors.custom36s}>
                {entry.durationMinutes} min
              </Text>
            </Flex>

            <Flex align="center" gap={4}>
              <IconUsers size={15} color={colors.primary} />
              <Text
                size="xs"
                fw={500}
                c={spotsLeft === 0 ? colors.red : colors.custom36s}
              >
                {entry.signupCount}/{entry.maxMembers}
              </Text>
            </Flex>
          </Flex>
        </div>
        <IconChevronRight size={20} color={colors.custom36p} style={{ opacity: 0.5, flexShrink: 0 }} />
      </div>
    </motion.div>
  );
};

const Classes: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const { data, isLoading } = useGroupClasses();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isLoading && data) {
      setTimeout(() => setShouldAnimate(true), 100);
    }
  }, [isLoading, data]);

  const classes = data?.classes ?? [];
  const enabled = data?.enabled ?? true;

  return (
    <motion.div
      className="p-4 mt-5"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
    >
      <motion.div variants={fadeInFromTop}>
        <Text
          size="lg"
          fw={500}
          c={colors.custom36s}
          className="text-center"
          mb={2}
        >
          {translate('classesTitle')}
        </Text>
      </motion.div>

      {isLoading && (
        <div className="flex justify-center py-10">
          <Loader color={colors.custom36p} />
        </div>
      )}

      {!isLoading && !enabled && (
        <motion.div variants={fadeInFromTop}>
          <Flex direction="column" align="center" gap={8} mt="xl" p="lg">
            <Text c={colors.dark} fw={500} ta="center">
              {translate('noClasses')}
            </Text>
          </Flex>
        </motion.div>
      )}

      {!isLoading && enabled && classes.length === 0 && (
        <motion.div variants={fadeInFromTop}>
          <Flex direction="column" align="center" gap={8} mt="xl" p="lg">
            <Text c={colors.dark} fw={500} ta="center" size="lg">
              {translate('noClasses')}
            </Text>
            <Text c={colors.dark} size="sm" ta="center">
              {translate('noClassesDesc')}
            </Text>
          </Flex>
        </motion.div>
      )}

      {!isLoading && enabled && classes.length > 0 && (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <Flex direction="column" gap={4}>
            {classes.map((entry) => (
              <ClassCard
                key={`${entry.classId}-${entry.instanceId}`}
                entry={entry}
                onClick={() => navigate(`/zajecia/${entry.instanceId}`)}
              />
            ))}
          </Flex>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Classes;
